import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';
import { Button, cx } from './ui';
import { dismissToast, resolveConfirm, useConfirm, useToasts } from '../state/ui';

const TONE_ICON: Record<string, IconName> = { info: 'message', success: 'check', error: 'alert' };
const TONE_COLOR: Record<string, string> = {
  info: 'var(--color-accent)',
  success: 'var(--color-success)',
  error: 'var(--color-danger)',
};

/** Bottom-center toast stack. Above everything except the timer alert. */
export function ToastHost() {
  const toasts = useToasts();
  if (toasts.length === 0) return null;

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[65] flex flex-col items-center gap-2 px-4 pb-[env(safe-area-inset-bottom)]">
      {toasts.map((t) => (
        <div
          key={t.id}
          onClick={() => dismissToast(t.id)}
          className="pointer-events-auto flex w-full max-w-sm items-center gap-3 overflow-hidden rounded-lg border border-border bg-surface-high px-4 py-3 shadow-[0_16px_40px_rgba(0,0,0,0.45)]"
          style={{ borderLeftColor: TONE_COLOR[t.tone], borderLeftWidth: 2 }}
          role="status"
        >
          <Icon name={TONE_ICON[t.tone] ?? 'message'} size={16} style={{ color: TONE_COLOR[t.tone] }} />
          <span className="flex-1 text-[14px] text-primary">{t.message}</span>
        </div>
      ))}
    </div>,
    document.body,
  );
}

/** Single confirm dialog driven by the module store. */
export function ConfirmHost() {
  const req = useConfirm();

  useEffect(() => {
    if (!req) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') resolveConfirm(false);
      if (e.key === 'Enter') resolveConfirm(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [req]);

  if (!req) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)' }}
      onMouseDown={(e) => e.target === e.currentTarget && resolveConfirm(false)}
    >
      <div className="w-full max-w-sm overflow-hidden rounded-lg border border-border bg-surface-high shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
        <div className="px-5 py-4">
          <h2 className="font-display text-[18px] text-primary">{req.title}</h2>
          {req.body && <p className="mt-1.5 text-[14px] leading-relaxed text-secondary">{req.body}</p>}
        </div>
        <div className="flex gap-2 border-t border-border-subtle px-5 py-3">
          <Button variant="ghost" className="flex-1" onClick={() => resolveConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant={req.danger ? 'danger' : 'primary'}
            className={cx('flex-1', req.danger && '!bg-danger/15')}
            onClick={() => resolveConfirm(true)}
          >
            {req.confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
